import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { useConfig } from "../lib/useConfig";
import { PageHeader } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { usePdfPreview } from "../components/PdfPreview";
import { Loader2, Eye, Printer, Download, Archive, Library as LibraryIcon, CheckCircle2, Clock, Stamp, RotateCcw } from "lucide-react";

const KINDS = [["", "Tous"], ["daily", "Quotidiens"], ["weekly", "Hebdomadaires"], ["monthly", "Mensuels"], ["consolidated", "Consolidés"]];
const KIND_CLS = { daily: "bg-[#FFF3EE] text-[#F05A28]", weekly: "bg-[#E0F2FE] text-[#0284C7]", monthly: "bg-emerald-50 text-emerald-700", consolidated: "bg-slate-100 text-slate-700" };
const KIND_LBL = { daily: "Quotidien", weekly: "Hebdomadaire", monthly: "Mensuel", consolidated: "Consolidé" };
const STATUSES = [["", "Tous statuts"], ["draft", "Brouillon"], ["submitted", "Soumis"], ["returned", "À corriger"], ["validated", "Validé"], ["consolidated", "Consolidé"]];
const OV = ["provincial_supervisor", "si_analyst", "program_manager", "admin"];

function Status({ s }) {
  const { t } = useLang();
  const map = {
    consolidated: ["text-[#0284C7]", Stamp, "Consolidé"], validated: ["text-emerald-700", CheckCircle2, "Validé"],
    submitted: ["text-amber-700", Clock, "Soumis"], returned: ["text-red-700", RotateCcw, "À corriger"],
  };
  const [cls, Icon, lbl] = map[s] || ["text-slate-500", Clock, "Brouillon"];
  return <span className={`inline-flex items-center gap-1 text-xs font-semibold ${cls}`}><Icon className="h-3.5 w-3.5" />{t(lbl)}</span>;
}

export default function Library() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const { data: cfg } = useConfig();
  const navigate = useNavigate();
  const pdf = usePdfPreview();
  const [f, setF] = useState({ kind: "", status: "", province: "", health_zone: "", author: "", date_from: "", date_to: "", q: "", archived: false });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const ov = OV.includes(user?.role);
  const provinces = Object.keys(cfg?.geo || {});
  const zones = f.province ? Object.values(cfg?.geo?.[f.province]?.territories || {}).flat() : [];

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries({ ...f, lang }).filter(([, v]) => v !== "" && v !== false));
      setData((await api.get("/library", { params })).data);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [f, lang]);

  const preview = (r) => pdf.show(() => api.get(r.pdf, { responseType: "blob", params: { lang } }).then((x) => x.data),
    { title: `${r.period_label} · ${r.scope_label}`, filename: `${KIND_LBL[r.kind]}_${r.period_key}.pdf` })
    .catch((e) => toast.error(apiErr(e.response?.data?.detail)));
  const download = async (r) => {
    try {
      const b = (await api.get(r.pdf, { responseType: "blob", params: { lang } })).data;
      const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = `${KIND_LBL[r.kind]}_${r.period_key}.pdf`; a.click(); URL.revokeObjectURL(u);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const archive = async (r) => {
    const url = r.kind === "monthly" ? `/monthly-reports/${r.id}/archive` : `/harmonized/reports/${r.id}/archive`;
    try { await api.post(url, { archived: !r.archived }); toast.success(t(r.archived ? "Rapport restauré." : "Rapport archivé.")); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const set = (k, v) => setF((s) => ({ ...s, [k]: v, ...(k === "province" ? { health_zone: "" } : {}) }));
  const c = data?.counts || {};
  const sel = "h-9 rounded-md border border-slate-200 bg-white px-2 text-sm";

  return (
    <div data-testid="library-page">
      <PageHeader eyebrow={t("Mémoire du programme")} title={t("Bibliothèque des rapports")}
        subtitle={t(ov ? "Tous les rapports générés par les agents, conservés et consultables à tout moment." : "Tous vos rapports générés, conservés et consultables à tout moment.")} testid="library-header" />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="library-counts">
        {[["total", "Rapports"], ["daily", "Quotidiens"], ["weekly", "Hebdomadaires"], ["monthly", "Mensuels"], ["status:validated", "Validés"]].map(([k, l]) => (
          <div key={k} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`lib-count-${k.replace(":", "-")}`}>
            <p className="text-[11px] uppercase tracking-wider text-slate-500">{t(l)}</p>
            <p className="text-2xl font-semibold text-slate-900">{c[k] || 0}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        {KINDS.map(([k, l]) => (
          <button key={k} onClick={() => set("kind", k)} data-testid={`lib-kind-${k || "all"}`}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${f.kind === k ? "bg-[#0F172A] text-white" : "bg-white border border-[#E2E8F0] text-slate-600 hover:bg-slate-50"}`}>{t(l)}</button>
        ))}
        <button onClick={() => set("archived", !f.archived)} data-testid="lib-archived"
          className={`px-3 py-1.5 rounded-md text-xs font-semibold inline-flex items-center gap-1.5 transition-colors ${f.archived ? "bg-slate-700 text-white" : "bg-white border border-[#E2E8F0] text-slate-600 hover:bg-slate-50"}`}>
          <Archive className="h-3.5 w-3.5" />{t("Archives")}
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-7 gap-2 mb-4" data-testid="library-filters">
        <Input placeholder={t("Rechercher…")} value={f.q} onChange={(e) => set("q", e.target.value)} className="h-9 lg:col-span-2" data-testid="lib-q" />
        <Input type="date" value={f.date_from} onChange={(e) => set("date_from", e.target.value)} className="h-9" data-testid="lib-from" />
        <Input type="date" value={f.date_to} onChange={(e) => set("date_to", e.target.value)} className="h-9" data-testid="lib-to" />
        {ov && (
          <select value={f.province} onChange={(e) => set("province", e.target.value)} className={sel} data-testid="lib-province">
            <option value="">{t("Toutes provinces")}</option>{provinces.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        )}
        {ov && (
          <select value={f.health_zone} onChange={(e) => set("health_zone", e.target.value)} className={sel} disabled={!f.province} data-testid="lib-zone">
            <option value="">{t("Toutes zones")}</option>{zones.map((z) => <option key={z} value={z}>{z}</option>)}
          </select>
        )}
        {ov && (
          <select value={f.author} onChange={(e) => set("author", e.target.value)} className={sel} data-testid="lib-author">
            <option value="">{t("Tous les auteurs")}</option>{(data?.authors || []).map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        )}
        <select value={f.status} onChange={(e) => set("status", e.target.value)} className={`${sel} ${ov ? "" : "lg:col-span-3"}`} data-testid="lib-status">
          {STATUSES.map(([k, l]) => <option key={k} value={k}>{t(l)}</option>)}
        </select>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm overflow-hidden">
        {loading && !data ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#F05A28]" /></div>
          : !data?.items?.length ? (
            <div className="p-12 text-center" data-testid="library-empty">
              <LibraryIcon className="h-9 w-9 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">{t("Aucun rapport ne correspond à ces critères.")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-[#F1F5F9] text-left">
                  {["Type", "Période", "Portée", "Auteur", "Statut", "Généré le", ""].map((h, i) => <th key={i} className="p-3 text-xs font-bold text-slate-600">{t(h)}</th>)}
                </tr></thead>
                <tbody>
                  {data.items.map((r) => (
                    <tr key={`${r.kind}-${r.id}`} className="border-t border-slate-100 hover:bg-[#F8FAFC]" data-testid={`lib-row-${r.id}`}>
                      <td className="p-3"><span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded ${KIND_CLS[r.kind]}`}>{t(KIND_LBL[r.kind])}</span>
                        {r.archived && <span className="ml-1.5 text-[10px] text-slate-500">{t("archivé")}</span>}</td>
                      <td className="p-3 font-medium text-slate-800">{r.period_label}</td>
                      <td className="p-3 text-slate-600">{r.scope_label}</td>
                      <td className="p-3 text-slate-600">{r.author_name || "—"}</td>
                      <td className="p-3"><Status s={r.status} /></td>
                      <td className="p-3 text-xs text-slate-500">{(r.created_at || "").slice(0, 10)}</td>
                      <td className="p-3 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" onClick={() => navigate(r.view)} data-testid={`lib-view-${r.id}`} title={t("Ouvrir")}><Eye className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => preview(r)} data-testid={`lib-preview-${r.id}`} title={t("Aperçu avant impression")}><Printer className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => download(r)} data-testid={`lib-pdf-${r.id}`} title={t("Télécharger PDF")}><Download className="h-4 w-4" /></Button>
                        {r.kind !== "consolidated" && (
                          <Button size="sm" variant="ghost" onClick={() => archive(r)} data-testid={`lib-archive-${r.id}`} title={t(r.archived ? "Restaurer" : "Archiver")}>
                            <Archive className={`h-4 w-4 ${r.archived ? "text-emerald-700" : "text-slate-500"}`} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
      {pdf.el}
    </div>
  );
}
